import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import './PostCard.css';

// Shared by the Blog and Features pages: the category filter row and the post cards.
// `color` is a palette name from themes/colors.css, e.g. 'primary' or 'info'.

interface TagFilterProps {
  tagColors: Record<string, string>;
  selected: string;
  onSelect: (tag: string) => void;
}

export const TagFilter: FC<TagFilterProps> = ({ tagColors, selected, onSelect }) => (
  <div className="tag-filter">
    {Object.entries(tagColors).map(([tag, color]) => (
      <button
        key={tag}
        onClick={() => onSelect(tag)}
        className={`tag-filter-button ${selected === tag ? 'active' : ''}`}
        aria-pressed={selected === tag}
        style={{
          backgroundColor: `var(--${color}200)`,
          color: `var(--${color}800)`,
          outlineColor: `var(--${color}500)`,
        }}
      >
        {tag}
      </button>
    ))}
  </div>
);

export const PostCardGrid: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="post-cards-container">{children}</div>
);

interface PostCardProps {
  to: string;
  image: string;
  // Fill the image area (for screenshots) instead of fitting a small logo
  imageCover?: boolean;
  // Overrides the cover crop (bottom edge, for screenshots), e.g. 'center' for photos
  imagePosition?: string;
  tag: string;
  color: string;
  title: string;
  subtitle: string;
  readTime: string;
  // Display date, e.g. "30 Jun 2025", and its YYYY-MM-DD form for <time>
  date: string;
  dateTime: string;
}

// A real link (not an onClick) so search engines and AI crawlers can follow it
export const PostCard: FC<PostCardProps> = ({ to, image, imageCover, imagePosition, tag, color, title, subtitle, readTime, date, dateTime }) => (
  <Link to={to} className="post-card">
    <img src={image} alt="" className={`post-card-image ${imageCover ? 'cover' : ''}`} style={{ backgroundColor: `var(--${color}200)`, objectPosition: imagePosition }} />
    <div className="post-card-text">
      <div className="post-card-tag-row">
        <div className="post-card-tag" style={{ backgroundColor: `var(--${color}200)`, color: `var(--${color}800)` }}>
          {tag}
        </div>
        <span className="post-card-read-time">
          <span><time dateTime={dateTime}>{date}</time> &nbsp;•&nbsp;</span> <span>{readTime} read</span>
        </span>
      </div>
      <h2 className="post-card-title">{title}</h2>
      <p className="post-card-subtitle">{subtitle}</p>
      <div className="post-card-button" style={{ color: `var(--${color}700)` }}>
        Read more &nbsp;→
      </div>
    </div>
  </Link>
);
